import React, { createContext, useState, useEffect } from "react";
import { getCachedTheme } from "../utils/appConfigCache";

const ThemeContext = createContext();

const getStorageKey = () => {
  const tenantSlug = localStorage.getItem("tenant_slug") || "default";
  return `app_theme:${tenantSlug}`;
};

export function ThemeProvider({ children }) {
  const [primaryColor, setPrimaryColor] = useState("#1a2b48");
  const [secondaryColor, setSecondaryColor] = useState("#f9b115");
  const [tertiaryColor, setTertiaryColor] = useState("#4b5563");

  // Load theme on mount - first from localStorage, then try backend
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const storageKey = getStorageKey();
        // Check localStorage first
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const theme = JSON.parse(stored);
          setPrimaryColor(theme.primaryColor);
          setSecondaryColor(theme.secondaryColor);
          setTertiaryColor(theme.tertiaryColor);
          applyColors(theme.primaryColor, theme.secondaryColor, theme.tertiaryColor);
          return;
        }

        // Try to fetch from backend if not in localStorage
        const response = await getCachedTheme();

        if (response?.success && response?.theme) {
          setPrimaryColor(response.theme.primaryColor);
          setSecondaryColor(response.theme.secondaryColor);
          setTertiaryColor(response.theme.tertiaryColor);
          applyColors(response.theme.primaryColor, response.theme.secondaryColor, response.theme.tertiaryColor);

          // Save to localStorage
          localStorage.setItem(storageKey, JSON.stringify(response.theme));
        }
      } catch (error) {
        console.warn("Theme loading error (using defaults):", error.message);
      }
    };

    loadTheme();

    // Listen for theme changes from other tabs/components
    const handleStorageChange = (e) => {
      if (e.key === getStorageKey() && e.newValue) {
        try {
          const theme = JSON.parse(e.newValue);
          setPrimaryColor(theme.primaryColor);
          setSecondaryColor(theme.secondaryColor);
          setTertiaryColor(theme.tertiaryColor);
          applyColors(theme.primaryColor, theme.secondaryColor, theme.tertiaryColor);
        } catch (error) {
          console.warn("Error parsing theme from storage event:", error);
        }
      }
    };

    // Custom event for same-tab updates
    const handleThemeChange = (e) => {
      const theme = e.detail;
      setPrimaryColor(theme.primaryColor);
      setSecondaryColor(theme.secondaryColor);
      setTertiaryColor(theme.tertiaryColor);
      applyColors(theme.primaryColor, theme.secondaryColor, theme.tertiaryColor);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("themeChange", handleThemeChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("themeChange", handleThemeChange);
    };
  }, []);

  // Apply colors to CSS variables
  const applyColors = (primary, secondary, tertiary) => {
    document.documentElement.style.setProperty("--primary-color", primary);
    document.documentElement.style.setProperty("--secondary-color", secondary);
    document.documentElement.style.setProperty("--tertiary-color", tertiary);
  };

  // Apply colors whenever they change
  useEffect(() => {
    applyColors(primaryColor, secondaryColor, tertiaryColor);
  }, [primaryColor, secondaryColor, tertiaryColor]);

  const value = {
    primaryColor,
    setPrimaryColor,
    secondaryColor,
    setSecondaryColor,
    tertiaryColor,
    setTertiaryColor,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeContext;
